import { useToast } from "@chakra-ui/react";
import { PathStyleExtension } from "@deck.gl/extensions";
import { IconLayer, PathLayer, PathLayerProps, PolygonLayer } from "@deck.gl/layers";
import area from "@turf/area";
import { MapLayerMouseEvent } from "maplibre-gl";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { XYLocation } from "@nens/deck-gl";
import { doesNewPointCrossExistingLines } from "@nens/shared-utils";

import { COLORS } from "@/config/deckgl-conts";

import { DrawLinePointPolygonProps } from "../types/draw-line-point-polygon-props";
import {
  DEFAULT_ICON_LAYER_PROPS,
  DEFAULT_PATH_LAYER_PROPS,
  DEFAULT_POLYGON_LAYER_PROPS,
} from "../utils/map-draw-consts";

function calculatePolygonCentroid(vertices: XYLocation[]): XYLocation {
  let area = 0;
  let centroidX = 0;
  let centroidY = 0;

  const numPoints = vertices.length;

  for (let i = 0; i < numPoints; i++) {
    const x1 = vertices[i][0];
    const y1 = vertices[i][1];
    const x2 = vertices[(i + 1) % numPoints][0]; // Wrap around to the first point
    const y2 = vertices[(i + 1) % numPoints][1];

    const crossProduct = x1 * y2 - x2 * y1;
    area += crossProduct;
    centroidX += (x1 + x2) * crossProduct;
    centroidY += (y1 + y2) * crossProduct;
  }

  area *= 0.5;
  centroidX = centroidX / (6 * area);
  centroidY = centroidY / (6 * area);

  return [centroidX, centroidY];
}

export interface DrawPolygonLayersProps {
  confirmDrawingCallback: () => void;
  removeLastPointCallback: () => void;
  points: XYLocation[];
  newLineIntersects: boolean;
  areaExceedsLimit: boolean;
  mouseLocation: XYLocation;
}

const getDrawPolygonLayers = ({
  points = [],
  newLineIntersects,
  areaExceedsLimit,
  confirmDrawingCallback,
  removeLastPointCallback,
  mouseLocation,
}: DrawPolygonLayersProps) => {
  if (!points.length) return [];

  const dashLine =
    mouseLocation && points.length > 0 ? [points[points.length - 1], mouseLocation] : null;

  return [
    new PolygonLayer({
      ...DEFAULT_POLYGON_LAYER_PROPS,
      id: "POLYGON-new-polygon-layer",
      getLineColor: COLORS.INVISIBLE,
      data: [points],
    } as PathLayerProps),
    new PathLayer({
      ...DEFAULT_PATH_LAYER_PROPS,
      id: "POLYGON-path-polygon-layer",
      data: [points],
      getColor: () => COLORS.BLUE,
    } as PathLayerProps),
    new PathLayer({
      ...DEFAULT_PATH_LAYER_PROPS,
      id: "POLYGON-dash-polygon-layer",
      data: dashLine ? [dashLine] : [],
      getColor: () => (!newLineIntersects && !areaExceedsLimit ? COLORS.BLUE : COLORS.RED),
      getDashArray: [10, 10],
      dashJustified: true,
      extensions: [new PathStyleExtension({ dash: true })],
    } as PathLayerProps),
    new IconLayer({
      ...DEFAULT_ICON_LAYER_PROPS,
      id: "POLYGON-confirm-polygon-layer",
      data: [points[0]],
      getIcon: () => "confirmNoAnchor",
      onClick: () => {
        confirmDrawingCallback();
        return false;
      },
      visible: points.length > 2,
    }),
    new IconLayer({
      ...DEFAULT_ICON_LAYER_PROPS,
      id: "POLYGON-cancel-polygon-layer",
      data: [points[points.length - 1]],
      getIcon: () => "cancelNoAnchor",
      onClick: () => {
        removeLastPointCallback();
        return true;
      },
    }),
  ];
};

export const useDrawPolygon = ({
  active,
  setRemovePopup,
  mouseLocation,
  isDeleting,
  acceptedGeometryTypes,
  formData,
  onChange,
  maxAreaSquareKm,
}: DrawLinePointPolygonProps) => {
  const toast = useToast();
  const intl = useIntl();

  const polygons = useMemo(() => {
    if (formData) {
      if (formData.type === "Polygon") return formData.coordinates;
      if (formData.type === "MultiPolygon") return formData.coordinates.map((poly) => poly[0]);
    }
    return [];
  }, [formData]);

  const [drawingPoints, setDrawingPoints] = useState<XYLocation[]>([]);

  if (!active && drawingPoints.length) setDrawingPoints([]);

  const areaExceedsLimit = (() => {
    if (!maxAreaSquareKm || !drawingPoints.length || !mouseLocation) return false;
    const polygonAreaSqMeters = area({
      type: "Polygon",
      coordinates: [[...drawingPoints, mouseLocation, drawingPoints[0]]],
    });
    return polygonAreaSqMeters / 1e6 > maxAreaSquareKm;
  })();

  const onMapClick = (e: MapLayerMouseEvent) => {
    const mouseLocation = e.lngLat.toArray();
    const newLineIntersects = doesNewPointCrossExistingLines(mouseLocation, drawingPoints);
    if (!active || isDeleting || newLineIntersects) return;
    if (areaExceedsLimit) {
      toast({
        title: intl.formatMessage({ id: "error", defaultMessage: "Error!" }),
        description: `The new point exceeds the maximum area of ${maxAreaSquareKm} square kilometers.`,
        status: "error",
      });
      return;
    }
    if (!acceptedGeometryTypes.includes("MultiPolygon") && polygons.length) {
      onChange({ type: "Polygon", coordinates: [[]] });
    }
    setDrawingPoints([...(drawingPoints ?? []), mouseLocation]);
  };

  const confirmDrawingCallback = () => {
    if (
      !drawingPoints ||
      // Do allow confirming when crossing first drawn line
      doesNewPointCrossExistingLines(mouseLocation, [...drawingPoints].splice(1))
    )
      return;

    const closedPoints = [...drawingPoints, drawingPoints[0]];

    setDrawingPoints([]);

    // If no formData, start with a Polygon
    if (!formData) {
      onChange({
        type: "Polygon",
        coordinates: [closedPoints],
      });
      return;
    }

    // If formData is a Polygon, convert to MultiPolygon unless it was empty
    if (formData.type === "Polygon") {
      if (!formData.coordinates[0] || !formData.coordinates[0].length) {
        onChange({
          type: "Polygon",
          coordinates: [closedPoints],
        });
        return;
      }

      onChange({
        type: "MultiPolygon",
        coordinates: [formData.coordinates, [closedPoints]],
      });
      return;
    }

    // If formData is a MultiPolygon, add new polygon
    if (formData.type === "MultiPolygon") {
      onChange({
        type: "MultiPolygon",
        coordinates: [...formData.coordinates, [closedPoints]],
      });
      return;
    }
  };

  const removeLastPointCallback = () => {
    setDrawingPoints(drawingPoints.slice(0, -1));
  };

  const newLineIntersects = useMemo(
    () => (mouseLocation ? doesNewPointCrossExistingLines(mouseLocation, drawingPoints) : false),
    [mouseLocation, drawingPoints]
  );

  const newPolygonLayers = getDrawPolygonLayers({
    points: drawingPoints,
    confirmDrawingCallback,
    removeLastPointCallback,
    newLineIntersects,
    areaExceedsLimit,
    mouseLocation,
  });

  const drawnPolygons = new PolygonLayer<XYLocation>({
    ...DEFAULT_POLYGON_LAYER_PROPS,
    id: "POLYGON-existing-polygon-layer",
    pickable: isDeleting,
    onClick: ({ object, index }) => {
      setRemovePopup({
        coordinates: calculatePolygonCentroid(object),
        type: "Polygon",
        onConfirm: () => {
          if (!formData) return;
          if (formData.type === "Polygon") {
            onChange({ type: "Polygon", coordinates: [[]] });
            return;
          }
          if (formData.type === "MultiPolygon") {
            const polygonCopy = [...polygons];
            polygonCopy.splice(index, 1);
            if (polygonCopy.length > 1) {
              onChange({
                type: "MultiPolygon",
                coordinates: polygonCopy.map((poly) => [poly]),
              });
              return;
            }
            onChange({ type: "Polygon", coordinates: [polygonCopy[0]] });
          }
        },
      });
    },
    data: [polygons],
  });

  return {
    polygonLayer: [drawnPolygons, ...newPolygonLayers],
    polygons,
    onMapClick,
  };
};
